/**
 *
 * Avatar
 *
 */

import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';

import CrossCircleIcon from 'assets/v2/CrossCircleIcon';
import Skeleton, { VARIANTS } from 'components/v2/Skeleton';
import { COLORS } from 'theme';

import { StyledAvatar, StyledBadge, StyledPhoto } from './Avatar.styles';

const getNameBasedColor = (initials) => {
  // We have only 12 colors, but there is 26 letters in alphabet, just replicate
  const colors = [].concat(...Array(3).fill(Object.keys(COLORS.avatar)));
  // 97 is `a` charCode
  const letterCharCode = initials.toLowerCase().charCodeAt(0) - 97;
  return COLORS.avatar[colors[letterCharCode]];
};

const extractInitialsFromName = (name) => {
  if (!name?.trim()) return '';
  const split = name.trim().split(' ');
  const initials = `${split[0][0]}${split[1]?.[0] || split[0][1] || ''}`;
  return initials.toUpperCase();
};

export const AVATAR_SETTINGS = Object.freeze({
  small: { width: 24, height: 24 },
  medium: { width: 40, height: 40 },
  large: { width: 56, height: 56 },
});

export const AVATAR_SIZES = Object.freeze({
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
});

const Avatar = ({
  isLoading,
  initials,
  name,
  closable,
  onClose,
  background,
  color = COLORS.white,
  size = AVATAR_SIZES.MEDIUM,
  photo,
}) => {
  const isClosable = size === AVATAR_SIZES.MEDIUM && closable;

  const avatarBackground = useMemo(
    () =>
      background ||
      getNameBasedColor(initials || extractInitialsFromName(name)),
    [background, initials, name],
  );

  const value = useMemo(
    () => initials || extractInitialsFromName(name),
    [initials, name],
  );

  if (isLoading) {
    return (
      <Skeleton
        width={AVATAR_SETTINGS[size].width}
        height={AVATAR_SETTINGS[size].height}
        variant={VARIANTS.CIRCULAR}
      />
    );
  }

  if (isClosable) {
    return (
      <StyledBadge
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={() => onClose()}
        badgeContent={<CrossCircleIcon sx={{ width: '20px' }} />}
      >
        {photo ? (
          <StyledPhoto src={photo} alt={name || initials} />
        ) : (
          <StyledAvatar
            alt={initials}
            size={AVATAR_SIZES[size]}
            sx={{
              bgcolor: avatarBackground,
              color,
              ...AVATAR_SETTINGS[size],
            }}
          >
            {value}
          </StyledAvatar>
        )}
      </StyledBadge>
    );
  }

  if (photo) {
    return <StyledPhoto src={photo} alt={name || initials} />;
  }

  return (
    <StyledAvatar
      alt={initials}
      size={size}
      sx={{
        bgcolor: avatarBackground,
        color,
        ...AVATAR_SETTINGS[size],
      }}
    >
      {value}
    </StyledAvatar>
  );
};

Avatar.propTypes = {
  isLoading: PropTypes.bool,
  initials: PropTypes.string,
  name: PropTypes.string,
  closable: PropTypes.bool,
  onClose: PropTypes.func,
  background: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.oneOf(Object.values(AVATAR_SIZES)),
  photo: PropTypes.node,
};

export default memo(Avatar);
